import { isFilled } from '@prismicio/client';
import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import { hasRichText } from '@publicss/utils';
import classNames from 'classnames';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { gsap } from 'gsap';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';

import {
  _ExternalLink,
  PageBodyBlock_Footer_CtaPrimary,
} from '../../../types/prismic';
import { colorPrismicMap } from '../../color-prismic-map';
import { usePageContext } from '../../providers/PageContext';
import { media } from '../../styles/media';
import resolveColorCK from '../../utils/resolveColorCK';
import { Button } from '../ui';

interface BlockFooterCTASliceProps {
  slice: {
    primary: PageBodyBlock_Footer_CtaPrimary;
  };
  sliceIndex: number;
}

const BlockFooterCTASlice: React.FunctionComponent<
  BlockFooterCTASliceProps
> = ({
  sliceIndex,
  slice: {
    primary: {
      color,
      title_start,
      title_middle,
      title_end,
      description,
      button,
      button_typeform_id,
      background,
      button_type = 'typeform',
      button_link,
    },
  },
}) => {
  const { siteTag } = usePageContext();

  useEffect(() => {
    const fadeElements = gsap.timeline();
    fadeElements.to('#block-cta', {
      opacity: 1,
      delay: 0.25,
    });

    fadeElements.from('#block-cta .fade-in-1', {
      duration: 0.6,
      opacity: 0.1,
      y: '5%',
    });

    fadeElements.from(
      '#block-cta .fade-in-2',
      {
        duration: 0.7,
        opacity: 0.1,
        stagger: {
          amount: 0.5,
        },
      },
      '-=0.25'
    );
  }, []);

  let colorInverse = colorPrismicMap['stone'];
  if (color === 'stone') {
    colorInverse = colorPrismicMap['herb'];
  }

  const twColor = resolveColorCK(color);
  const backgroundImgData = getImage(background);

  return (
    <Section siteTag={siteTag} id="block-cta">
      <div className="container">
        <div className="mt-40 grid sm:grid-cols-4 md:mt-0 md:grid-cols-8 lg:grid-cols-12">
          <div
            className={`relative col-span-5 col-start-1
                pb-16 pt-16 md:ps-16 lg:col-start-8 lg:pb-24 lg:ps-24
                lg:pt-24 bg-${twColor} bg--right fade-in-1`}>
            <div className="relative z-10">
              <p
                className={classNames(
                  'color--white fade-up mb-10 pr-4 md:mb-12',
                  siteTag === 'site:kitchenvalley' ? 'h2-kv' : 'h2'
                )}>
                {' '}
                <PrismicRichText field={title_start.richText} />
                <span className={`color--${colorInverse}`}>
                  <PrismicRichText field={title_middle.richText} />
                </span>{' '}
                <PrismicRichText field={title_end.richText} />
              </p>
              {isFilled.richText(description?.richText) && (
                <p className={`color--${colorInverse} mb-10 md:mb-12`}>
                  <PrismicRichText
                    field={description.richText}
                    components={{
                      label: ({ children, node }) => {
                        switch (node.data.label) {
                          case 'Emphasis':
                            return (
                              <span className="color--white">{children}</span>
                            );
                          default:
                            return <span>{children}</span>;
                        }
                      },
                    }}
                  />
                </p>
              )}
              <div className="mr-8 md:mr-0">
                {button_type === 'link' &&
                (button_link as _ExternalLink)?.url ? (
                  <Button
                    id={`cta-footer-cta-slice-${sliceIndex}`}
                    variant="inverse"
                    href={(button_link as _ExternalLink).url}>
                    {asText(button.richText)}
                  </Button>
                ) : button_type === 'typeform' && button_typeform_id ? (
                  <Button
                    id={`cta-footer-cta-slice-${sliceIndex}`}
                    globalTypeform
                    typeform_id={button_typeform_id}
                    variant="inverse">
                    {asText(button.richText)}
                  </Button>
                ) : (
                  hasRichText(button.richText) && (
                    <Button
                      id={`cta-footer-cta-slice-${sliceIndex}`}
                      globalTypeform
                      variant="inverse">
                      {asText(button.richText)}
                    </Button>
                  )
                )}
              </div>
            </div>
            <div className={`bg--right-overlay bg-${twColor}`}></div>
          </div>
        </div>
      </div>
      <div className="bg--hero--holder fade-in-2 rtl:scale-x-[-1]">
        {backgroundImgData && (
          <GatsbyImage className="bg--hero" image={backgroundImgData} alt="" />
        )}
      </div>
    </Section>
  );
};
export default BlockFooterCTASlice;

const Section = styled.section<{ siteTag: string }>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  opacity: 0;
  min-height: 630px;
  .h2,
  .h2-kv {
    font-size: clamp(2.5rem, 5.2vw + 0.4rem, 2.9rem);
    p {
      line-height: 92%;
    }
  }
  .h2-kv {
    font-size: clamp(2rem, 5.2vw + 0.4rem, 2.2rem);
  }
  .bg--hero--holder {
    position: absolute;
    z-index: -1;
    top: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 630px;
    overflow: hidden;
    @media ${media.mdUp} {
      width: 100%;
      height: 100%;
    }
  }
  .bg--hero {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    object-position: top -50px center;
    overflow: hidden;
    @media ${media.mdUp} {
      object-position: bottom center;
    }
  }
  .bg--right {
    .bg--right-overlay {
      position: absolute;
      z-index: 1;
      content: '';
      bottom: 0;
      inset-inline-start: -100%;
      height: 100%;
      width: 200%;
      @media ${media.mdUp} {
        inset-inline-end: -100%;
        inset-inline-start: auto;
      }
    }
  }

  ${({ siteTag }) => {
    if (siteTag === 'site:profood-solutions') {
      return css`
        button,
        button:hover,
        button:active {
          color: #000 !important;
          filter: invert(1);
        }
      `;
    }
  }}
`;

`;
    }
  }}
`;
